/**
 * Top-level assembly for the editor V2 rewrite (task 9).
 *
 * First point the modular V2 editor renders end-to-end: wires the store,
 * the load/save/library hook (`useEditorProject`), the headless playback
 * driver (`useEditorPlayback`) and the render-progress SSE hook
 * (`useEditorRenderProgress`) into the full chrome — top bar, left icon
 * rail + tool/chat panel, center preview, right details panel and the
 * bottom timeline. Replaces the monolithic `VideoEditor.tsx` in the editor
 * page (see `EditorEntry.tsx`).
 */
'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuthToken } from '@/hooks/useAuthToken';
import { useI18n } from '@/contexts/I18nContext';
import { selectDuration, useEditorStore, type ToolId } from '@/lib/editor/editorStore';
import { EditorIconRail, type EditorLeftMode } from './EditorIconRail';
import { EditorTopBar } from './EditorTopBar';
import { EditorChatSidebar } from './EditorChatSidebar';
import { EditorLeftPanel } from './EditorLeftPanel';
import { EditorPreview } from './EditorPreview';
import { EditorDetailsPanel } from './EditorDetailsPanel';
import { EditorTimeline } from './EditorTimeline';
import { RenderProgressCard } from './RenderProgressCard';
import { useEditorProject } from './hooks/useEditorProject';
import { useEditorPlayback } from './hooks/useEditorPlayback';
import { useEditorRenderProgress } from './hooks/useEditorRenderProgress';
import { useGenerationPoller } from './hooks/useGenerationPoller';
import { useCannyGenStream } from './hooks/useCannyGenStream';
import { useGenerationModels } from './hooks/useGenerationModels';
import { packTimelineLanes } from '@/lib/editor/timelineLanes';
import type { EditorExportOptions } from '@/types/editor';
import { stepTimeByFrames } from '@/lib/editor/frameGrid';

// Same `mm:ss.t` format the preview/timeline use, so the top-bar timecode
// reads consistently with the rest of the editor.
function formatTimecode(seconds: number) {
  if (!Number.isFinite(seconds) || seconds <= 0) return '00:00.0';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  const tenth = Math.floor((seconds % 1) * 10);
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}.${tenth}`;
}

// Thin vertical drag handle for resizing the side panels.
function PanelResizeHandle({ onMouseDown }: { onMouseDown: (e: React.MouseEvent) => void }) {
  return (
    <div
      onMouseDown={onMouseDown}
      role="separator"
      aria-orientation="vertical"
      title="Потяните, чтобы изменить ширину"
      className="group flex w-1.5 shrink-0 cursor-col-resize items-center justify-center border-x border-white/10 bg-white/[0.02] transition hover:bg-brand-500/20"
    >
      <span className="h-10 w-0.5 rounded-full bg-white/15 transition group-hover:bg-brand-400/70" />
    </div>
  );
}

export function EditorShell({
  projectId,
  onExitToHub,
}: {
  projectId: string;
  onExitToHub: () => void;
}) {
  const { authedFetch } = useAuthToken();
  const { t } = useI18n();

  // Load draft + media library + autosave (blob→S3 upload runs inside save()).
  const { save } = useEditorProject(projectId);
  // Headless playback driver — advances the store's currentTime while playing.
  useEditorPlayback();
  // Canny → Supercomputer generation poller — must run regardless of
  // `leftMode` (the chat sidebar unmounts when the user switches panels),
  // so it lives here rather than inside the sidebar.
  useGenerationPoller();
  useCannyGenStream(); // SSE push (webhook-driven); poller above is the fallback
  // Warm the WaveSpeed model catalog cache as soon as the editor opens, so
  // it's already resolved by the time the user proposes a generation.
  useGenerationModels();

  // ---- store reads
  const leftMode = useEditorStore((s) => s.leftMode);
  const currentTime = useEditorStore((s) => s.currentTime);
  const duration = useEditorStore((s) => selectDuration(s));
  const renderJobId = useEditorStore((s) => s.renderJobId);
  const overlayLaneCount = useEditorStore((s) => Math.max(1, packTimelineLanes(s.layers).length));

  // ---- store actions
  const setLeftMode = useEditorStore((s) => s.setLeftMode);
  const setActiveTool = useEditorStore((s) => s.setActiveTool);

  // Global undo/redo hotkeys — EditorShell is always mounted (unlike
  // EditorTimeline's toolbar buttons, which only cover click access), so the
  // listener lives here. Cmd/Ctrl+Z → undo, Cmd/Ctrl+Shift+Z or Ctrl+Y →
  // redo. Ignored while the user is typing in an input/textarea/
  // contentEditable so it doesn't hijack normal text-editing undo.
  useEffect(() => {
    const isTextInput = (target: EventTarget | null) => {
      if (!(target instanceof HTMLElement)) return false;
      const tag = target.tagName;
      return tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable;
    };
    // Any focusable interactive control (panel buttons, links, selects, ARIA
    // widgets). Destructive/playback/nav keys must NOT fire while one of these
    // has focus, or Backspace/Delete on a focused panel button silently deletes
    // the selected clip/layer and Space hijacks the button's native activation.
    const isInteractive = (target: EventTarget | null) => {
      if (!(target instanceof HTMLElement)) return false;
      const tag = target.tagName;
      if (tag === 'BUTTON' || tag === 'A' || tag === 'SELECT') return true;
      const role = target.getAttribute('role') || '';
      return ['button', 'slider', 'tab', 'menuitem', 'option', 'switch', 'checkbox'].includes(role);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (isTextInput(e.target)) return;
      const key = e.key.toLowerCase();
      // Undo/redo stay global (harmless, non-destructive-directional).
      if ((e.metaKey || e.ctrlKey) && key === 'z') {
        e.preventDefault();
        const { undo, redo } = useEditorStore.getState();
        if (e.shiftKey) redo();
        else undo();
        return;
      }
      if (e.ctrlKey && key === 'y') {
        e.preventDefault();
        useEditorStore.getState().redo();
        return;
      }
      // From here down: destructive + playback + navigation shortcuts — skip
      // when the user is operating an interactive control.
      if (isInteractive(e.target)) return;
      const state = useEditorStore.getState();
      const duration = selectDuration(state);
      if (e.key === ' ') {
        e.preventDefault();
        if (state.clips.length) state.setIsPlaying(!state.isPlaying);
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault();
        const direction = e.key === 'ArrowRight' ? 1 : -1;
        const frames = e.shiftKey ? 30 : 1;
        state.setCurrentTime(Math.min(duration, stepTimeByFrames(state.currentTime, direction * frames)));
      } else if (e.key === 'Home') {
        e.preventDefault();
        state.setCurrentTime(0);
      } else if (e.key === 'End') {
        e.preventDefault();
        state.setCurrentTime(duration);
      } else if (key === 's') {
        e.preventDefault();
        state.splitClipAtPlayhead();
      } else if ((e.metaKey || e.ctrlKey) && key === 'd') {
        e.preventDefault();
        if (state.selectedClipId) state.duplicateClip(state.selectedClipId);
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        if (state.selectedLayerId) state.removeLayer(state.selectedLayerId);
        else if (state.selectedClipId) state.removeClip(state.selectedClipId);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const [renderStarting, setRenderStarting] = useState(false);

  // Resizable timeline height (drag its top edge). Default fits toolbar +
  // ruler + all 4 tracks so nothing clips; the top area shrinks to fit.
  const [timelineHeight, setTimelineHeight] = useState(256);
  const previousOverlayLaneCountRef = useRef(0);

  // A multi-layer template can add 3–5 simultaneous overlays. Grow the
  // timeline when that happens so every generated lane is immediately visible;
  // users may still shrink it manually afterwards with the resize handle.
  useEffect(() => {
    const previousCount = previousOverlayLaneCountRef.current;
    previousOverlayLaneCountRef.current = overlayLaneCount;
    if (overlayLaneCount <= previousCount) return;
    const requiredHeight = 178 + overlayLaneCount * 36;
    const maxHeight = Math.round(window.innerHeight * 0.6);
    setTimelineHeight((current) => Math.max(current, Math.min(requiredHeight, maxHeight)));
  }, [overlayLaneCount]);
  const onTimelineResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const startY = e.clientY;
    const startH = timelineHeight;
    const maxH = Math.round(window.innerHeight * 0.6);
    const onMove = (ev: MouseEvent) => {
      const next = Math.max(160, Math.min(maxH, startH + (startY - ev.clientY)));
      setTimelineHeight(next);
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [timelineHeight]);

  // Resizable side panels (drag their inner edge).
  const [leftWidth, setLeftWidth] = useState(308);
  const [rightWidth, setRightWidth] = useState(300);
  const startPanelResize = useCallback(
    (side: 'left' | 'right', e: React.MouseEvent) => {
      e.preventDefault();
      const startX = e.clientX;
      const startW = side === 'left' ? leftWidth : rightWidth;
      const onMove = (ev: MouseEvent) => {
        const delta = side === 'left' ? ev.clientX - startX : startX - ev.clientX;
        const next = Math.max(220, Math.min(560, startW + delta));
        if (side === 'left') setLeftWidth(next);
        else setRightWidth(next);
      };
      const onUp = () => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
      };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    },
    [leftWidth, rightWidth]
  );

  // Render-progress SSE/poll (jobId lives in the store, set by onRender).
  const { progress, jobStatus, renderVideoId, errorMessage } = useEditorRenderProgress(
    authedFetch as unknown as typeof fetch,
    renderJobId,
    t
  );

  const timecode = `${formatTimecode(currentTime)} / ${formatTimecode(duration)}`;
  const jobActive = Boolean(renderJobId) && jobStatus !== 'completed' && jobStatus !== 'failed';
  const rendering = renderStarting || jobActive;

  const handleLeftMode = useCallback(
    (mode: EditorLeftMode) => {
      setLeftMode(mode);
      if (mode !== 'chat') setActiveTool(mode as ToolId);
    },
    [setLeftMode, setActiveTool]
  );

  // Import = focus the Media panel (user picks/imports files there).
  const handleImport = useCallback(() => {
    setLeftMode('media');
    setActiveTool('media');
  }, [setLeftMode, setActiveTool]);

  const handleSplit = useCallback(() => {
    useEditorStore.getState().splitClipAtPlayhead();
  }, []);

  // Render: save first (persists local blobs to S3), then POST the render job
  // and hand the jobId to the store so useEditorRenderProgress tracks it.
  const handleRender = useCallback(async (exportOptions: EditorExportOptions) => {
    setRenderStarting(true);
    try {
      await save();
      const pid = useEditorStore.getState().projectId ?? projectId;
      const res = await authedFetch(`/api/editor-projects/${pid}/render`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ exportOptions }),
      });
      const json = await res.json();
      if (res.ok && json?.success && json.job?.id) {
        useEditorStore.getState().setRenderJobId(json.job.id);
      }
    } catch {
      // Best-effort; the user can retry from the top bar.
    } finally {
      setRenderStarting(false);
    }
  }, [authedFetch, projectId, save]);

  const handleCancelRender = useCallback(async () => {
    const id = useEditorStore.getState().renderJobId;
    if (!id) return;
    try {
      await authedFetch(`/api/editor-render-jobs/${id}/cancel`, { method: 'POST' });
    } catch {
      /* ignore */
    }
  }, [authedFetch]);

  const handleRetryRender = useCallback(async () => {
    const id = useEditorStore.getState().renderJobId;
    if (!id) return;
    try {
      const res = await authedFetch(`/api/editor-render-jobs/${id}/retry`, { method: 'POST' });
      const json = await res.json();
      if (json?.success && json.job?.id) useEditorStore.getState().setRenderJobId(json.job.id);
    } catch {
      /* ignore */
    }
  }, [authedFetch]);

  return (
    <div className="editor-scope flex h-full min-h-0 overflow-hidden bg-[#0b0b0c] text-white">
      {/* Full-height left rail (back arrow at top). */}
      <EditorIconRail active={leftMode} onSelect={handleLeftMode} onExitToHub={onExitToHub} />

      {/* Everything to the right of the rail. */}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <EditorTopBar
          onImport={handleImport}
          onSplit={handleSplit}
          onRender={handleRender}
          onSave={() => void save()}
          rendering={rendering}
        />

        <div className="flex min-h-0 flex-1">
          {/* Left tool / chat column */}
          <div
            className="flex min-h-0 shrink-0 flex-col overflow-hidden bg-[#121214]"
            style={{ width: leftWidth }}
          >
            {leftMode === 'chat' ? <EditorChatSidebar projectId={projectId} /> : <EditorLeftPanel />}
          </div>
          <PanelResizeHandle onMouseDown={(e) => startPanelResize('left', e)} />

          {/* Center preview */}
          <EditorPreview />

          {/* Right details column */}
          <PanelResizeHandle onMouseDown={(e) => startPanelResize('right', e)} />
          <div className="min-h-0 shrink-0" style={{ width: rightWidth }}>
            <EditorDetailsPanel />
          </div>
        </div>

        {/* Bottom timeline — resizable via the top drag handle; content fills. */}
        <div className="flex shrink-0 flex-col" style={{ height: timelineHeight }}>
          <div
            onMouseDown={onTimelineResizeStart}
            role="separator"
            aria-orientation="horizontal"
            title="Потяните, чтобы изменить высоту таймлайна"
            className="group flex h-2 shrink-0 cursor-row-resize items-center justify-center border-t border-white/10 bg-white/[0.03] transition hover:bg-brand-500/20"
          >
            <span className="h-0.5 w-10 rounded-full bg-white/20 transition group-hover:bg-brand-400/70" />
          </div>
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden [&>section]:min-h-0 [&>section]:flex-1">
            <EditorTimeline />
          </div>
        </div>
      </div>

      {/* Render progress (floating, doesn't disturb the flex layout). */}
      {renderJobId && (
        <div className="fixed bottom-4 right-4 z-[60] w-80">
          <RenderProgressCard
            status={jobStatus}
            progress={progress}
            videoId={renderVideoId}
            onCancel={handleCancelRender}
            onRetry={handleRetryRender}
            t={t}
            errorMessage={errorMessage}
          />
        </div>
      )}
    </div>
  );
}
